import { useEffect, useRef, useState } from "react"
import { formatCurrency } from "src/shared/utils/formatCurrency"
import {
  ScheduledRange,
  D,
  DAY,
  Flight,
  Segment,
  SpendPattern,
  addDays,
  dayCount,
  fmtDay,
  fmtRange,
  iso,
  payDays,
} from "./segmentEngine"
import { KeyDate } from "./keyDates"

/* One sentence, used wherever an elapsed window explains itself: the card tag, the timeline
   block, and the Networks table header. */
export const ENDED_TIP = "Past days cannot be changed. Changes apply from today forward."
import { Formula } from "./Formula"
import { todo } from "./completion"

const PATTERN_LABEL: Record<SpendPattern, string> = {
  even: "Even",
  monthly: "Monthly",
  custom: "Custom",
}

const PATTERNS: SpendPattern[] = ["even", "monthly", "custom"]

export type SegmentStripProps = {
  segments: Segment[]
  selectedId: string
  onSelect: (id: string) => void
  editing: boolean
  /** The tab being viewed. In Edit this is preview only. */
  mode: SpendPattern
  onMode: (p: SpendPattern) => void
  /** The committed pattern, and the one Save will commit. */
  active: SpendPattern
  draftActive: SpendPattern
  onSetActive: (p: SpendPattern) => void
  view: "cards" | "timeline"
  onView: (v: "cards" | "timeline") => void
  netAmount: number
  flight: Flight
  windowStart: string
  windowEnd: string
  fundedDays: number
  scheduledRanges: readonly ScheduledRange[]
  /** Today, as YYYY-MM-DD. A window that ended before it cannot be edited. */
  today: string
  keyDates: KeyDate[]
  onUpdate: (id: string, patch: Partial<Segment>) => void
  onDelete: (id: string) => void
  onAdd: () => void
  onAddAt: (date: string) => void
  notice?: React.ReactNode
}

export function SegmentStrip(props: SegmentStripProps) {
  const allocated = props.segments.reduce((a, s) => a + s.amount, 0)

  return (
    <div className="seg-strip">
      <div className="seg-strip__bar">
        <div className="seg-strip__controls">
        <span className="seg-strip__label">Spend Pattern</span>
        <PatternInfo />

        {/* Outside Edit the committed pattern only, with no switcher. In Edit the tabs
            preview; the active pattern moves on an explicit commit plus Save. */}
        {props.editing ? (
          <>
            <div className="seg-pattern" role="tablist">
              {PATTERNS.map((p) => (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={props.mode === p}
                  className={`seg-pattern__option${props.mode === p ? " seg-pattern__option--on" : ""}`}
                  onClick={() => props.onMode(p)}
                >
                  {PATTERN_LABEL[p]}
                  {p === props.draftActive ? " · Active" : ""}
                </button>
              ))}
            </div>
            {props.mode === props.draftActive ? (
              <span className="seg-active-tag">
                {props.draftActive === props.active ? "Active pattern" : "Active on save"}
              </span>
            ) : (
              <>
                <span className="seg-strip__preview-note">Previewing — not active</span>
                <button
                  type="button"
                  className="ds-btn ds-btn--sm ds-btn--secondary"
                  onClick={() => props.onSetActive(props.mode)}
                  {...todo("draft", "Moves the draft's active pattern. Nothing records it — needs Launch.spend_pattern.")}
                >
                  Set as active pattern
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <span className="seg-strip__active-name">{PATTERN_LABEL[props.mode]}</span>
            <span className="seg-active-tag">Active pattern</span>
          </>
        )}

        <span className="seg-strip__divider" />
        <span className="seg-strip__label">View By</span>
        <div className="seg-pattern">
          {(["cards", "timeline"] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={`seg-pattern__option${props.view === v ? " seg-pattern__option--on" : ""}`}
              onClick={() => props.onView(v)}
            >
              {v === "cards" ? "Cards" : "Timeline"}
            </button>
          ))}
        </div>

        </div>
        <Reconcile amount={props.netAmount} allocated={allocated} />
      </div>

      {props.notice}

      {props.view === "cards" ? <CardsView {...props} /> : <TimelineView {...props} />}
    </div>
  )
}

function PatternInfo() {
  return (
    <InfoTip
      info={
        <>
          <div style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
            Choose one pacing pattern for this campaign
          </div>
          <div>
            <b>Even</b> — one steady daily rate for the whole flight
          </div>
          <div>
            <b>Monthly</b> — different spend by calendar month
          </div>
          <div>
            <b>Custom</b> — exact date ranges, including planned gaps or pauses
          </div>
        </>
      }
    />
  )
}

/**
 * Compares against the live Amount, not the campaign's stored figure: Amount moves with
 * margin, so the stored one is only right at the saved margin.
 */
function Reconcile({ amount, allocated }: { amount: number; allocated: number }) {
  const diff = amount - allocated
  const off = Math.abs(diff) > 0.5
  const over = diff < -0.5
  // A bar rather than a tinted box (design 10/9): how close the windows are to the
  // campaign's Amount is a proportion, and a proportion is better shown than described.
  // Purple while filling, green at exactly 100%, red once past it.
  const filled = amount > 0 ? Math.min(100, (allocated / amount) * 100) : 0
  const tone = over ? "over" : off ? "under" : "full"

  return (
    <div className="seg-reconcile">
      <Readout label="Amount" value={formatCurrency(amount)} />
      <InfoTip
        info="Whether the segments add up to the campaign's Amount."
        formula={`Allocated = Σ segment amounts = ${formatCurrency(allocated)}`}
        method="A shortfall is money not yet placed in any window; an overage is more placed than the campaign holds."
      />
      <span className="seg-reconcile__sep" />
      <div className="seg-reconcile__progress">
        <span className="seg-reconcile__allocated">Allocated {formatCurrency(allocated)}</span>
        <span
          className={`seg-reconcile__bar seg-reconcile__bar--${tone}`}
          role="progressbar"
          aria-valuenow={Math.round(filled)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Share of the campaign's Amount placed in a spend window"
        >
          <span style={{ width: `${over ? 100 : filled}%` }} />
        </span>
      </div>
      {off && !over && (
        <span className="seg-reconcile__flag">
          Campaign unallocated {formatCurrency(diff)}
          <InfoTip info="Part of this campaign's amount is not yet in a spend window. It will not spend unless you add it to one." />
        </span>
      )}
      {over && (
        <span className="seg-reconcile__flag seg-reconcile__flag--over">
          <span className="seg-reconcile__bang">!</span>
          {formatCurrency(-diff)} over Amount
        </span>
      )}
    </div>
  )
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="seg-reconcile__readout">
      <span className="seg-reconcile__key">{label}</span>
      <span className="seg-reconcile__value">{value}</span>
    </div>
  )
}

/* ---------- cards ---------- */

function CardsView(props: SegmentStripProps) {
  const items: React.ReactNode[] = []

  props.segments.forEach((s, i) => {
    if (i > 0) {
      const prevEnd = props.segments[i - 1].end
      const gap = dayCount(addDays(prevEnd, 1), addDays(s.start, -1))
      if (gap > 0) items.push(<GapDivider key={"g" + s.id} days={gap} />)
    }
    items.push(
      <SegCard
        key={s.id}
        s={s}
        selected={s.id === props.selectedId}
        onSelect={() => props.onSelect(s.id)}
        editing={props.editing}
        mode={props.mode}
        flight={props.flight}
        fundedDays={props.fundedDays}
        scheduledRanges={props.scheduledRanges}
        today={props.today}
        onUpdate={(patch) => props.onUpdate(s.id, patch)}
        onDelete={() => props.onDelete(s.id)}
      />,
    )
  })

  return (
    <div className="seg-cards">
      {items}
      {props.mode === "custom" && props.editing && (
        <button type="button" className="seg-add" onClick={props.onAdd}>
          + Add
        </button>
      )}
    </div>
  )
}

function SegCard({
  s,
  selected,
  onSelect,
  editing,
  mode,
  flight,
  fundedDays,
  scheduledRanges,
  today,
  onUpdate,
  onDelete,
}: {
  s: Segment
  selected: boolean
  onSelect: () => void
  editing: boolean
  mode: SpendPattern
  flight: Flight
  fundedDays: number
  scheduledRanges: readonly ScheduledRange[]
  today: string
  onUpdate: (patch: Partial<Segment>) => void
  onDelete: () => void
}) {
  const days = dayCount(s.start, s.end)
  const pd = payDays(s, scheduledRanges, fundedDays)
  /*
      A window that has already ended is read-only. Its dollars have been spent; editing
      them would rewrite history rather than change anything that is going to happen, and
      the reflow rule the whole epic rests on depends on elapsed windows keeping what they
      hold. A window straddling today keeps its start fixed for the same reason -- the days
      before today already happened -- but its end is still ahead and still editable.
  */
  const past = D(s.end) < D(today)
  const startLocked = D(s.start) < D(today)
  const datesEditable = editing && mode !== "monthly" && !past
  // Nothing can be moved into the past, so the earliest a window may start is today.
  const minStart = D(today) > D(flight.start) ? today : flight.start

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      className={`seg-card${selected ? " seg-card--on" : ""}`}
    >
      <div className="seg-card__head">
        {datesEditable ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            {startLocked ? (
              <span className="seg-card__range">{fmtDay(s.start)}</span>
            ) : (
              <input
                type="date"
                className="seg-date"
                value={s.start}
                min={minStart}
                max={s.end}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  e.target.value &&
                  onUpdate({
                    start:
                      e.target.value > s.end ? s.end : e.target.value < minStart ? minStart : e.target.value,
                  })
                }
              />
            )}
            <span style={{ color: "var(--slate-400)", fontSize: 11 }}>–</span>
            <input
              type="date"
              className="seg-date"
              value={s.end}
              min={D(s.start) > D(today) ? s.start : today}
              max={flight.end}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                e.target.value &&
                onUpdate({
                  end:
                    e.target.value < s.start
                      ? s.start
                      : D(e.target.value) < D(today)
                        ? today
                        : e.target.value,
                })
              }
            />
          </span>
        ) : (
          <span className="seg-card__range">{fmtRange(s.start, s.end)}</span>
        )}
        <span className="seg-card__days">{days}d</span>
        {past && (
          <span className="seg-card__ended" title={ENDED_TIP}>
            <i className="bi bi-lock-fill" />
            Ended
          </span>
        )}
        {editing && mode === "custom" && !past && (
          <button
            type="button"
            className="seg-card__drop"
            title="Delete segment"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          >
            ✕
          </button>
        )}
      </div>

      <div style={{ marginTop: 7 }}>
        {editing && !past ? (
          <AmountField value={s.amount} onChange={(v) => onUpdate({ amount: v })} />
        ) : (
          <span className="seg-card__amount">{formatCurrency(s.amount)}</span>
        )}
      </div>

      <div className="seg-card__rate">
        {formatCurrency(s.amount / pd)} a day
        {pd < days ? ` over ${pd} funded days` : ""}
      </div>
    </div>
  )
}

function GapDivider({ days }: { days: number }) {
  return (
    <div className="seg-gap" title="No spend">
      <span>NO SPEND · {days}d</span>
    </div>
  )
}

/**
 * Holds a string draft while focused: coercing every keystroke through Number() after
 * stripping non-digits ate the decimal point, so "8500.00" committed as 850000.
 *
 * Shown to the cent when it is not being typed into. The resolved amount arrives with
 * whatever precision the server's Decimal division produced -- 3679.2499... on the
 * production dump, and `allocation` itself carries half-cents -- and every other figure on
 * this screen passes through `formatCurrency`, so this field was the one place the extra
 * digits surfaced. Display only: the draft is untouched while the operator types, and what
 * Save sends is still the number the field was given.
 */
function AmountField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null)
  const shown = Number.isFinite(value) ? value.toFixed(2) : ""

  return (
    <span
      className="seg-field seg-field--amount"
      style={{ width: 96 }}
      {...todo("draft", "Edits the draft. The pace CRUD endpoints exist; the save path does not.")}
    >
      <span className="seg-field__prefix">$</span>
      <input
        value={draft !== null ? draft : shown}
        inputMode="decimal"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const clean = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
          setDraft(clean)
          const n = parseFloat(clean)
          onChange(isNaN(n) ? 0 : Math.max(0, n))
        }}
        onBlur={() => setDraft(null)}
      />
    </span>
  )
}

/* ---------- timeline ---------- */

function TimelineView(props: SegmentStripProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { windowStart, windowEnd, segments, flight } = props
  const total = dayCount(windowStart, windowEnd)
  const pct = (d: string) => ((D(d) - D(windowStart)) / DAY / total) * 100

  const [drag, setDrag] = useState<{ id: string; edge: "start" | "end" } | null>(null)
  const [canAddHere, setCanAddHere] = useState(false)

  const dateAtX = (clientX: number) => {
    const r = ref.current!.getBoundingClientRect()
    const i = Math.round(((clientX - r.left) / r.width) * total)
    return addDays(windowStart, Math.max(0, Math.min(total - 1, i)))
  }

  useEffect(() => {
    if (!drag) return
    const move = (e: MouseEvent) => {
      const d = dateAtX(e.clientX)
      const s = segments.find((x) => x.id === drag.id)
      if (!s) return
      // Neither edge may be dragged into the past: those days have already spent.
      if (D(d) < D(props.today)) return
      if (drag.edge === "start") {
        if (D(d) <= D(s.end)) props.onUpdate(s.id, { start: d })
      } else if (D(d) >= D(s.start)) props.onUpdate(s.id, { end: d })
    }
    const up = () => setDrag(null)
    document.addEventListener("mousemove", move)
    document.addEventListener("mouseup", up)
    return () => {
      document.removeEventListener("mousemove", move)
      document.removeEventListener("mouseup", up)
    }
  }, [drag, segments])

  const months: { label: string; left: number; width: number }[] = []
  {
    let cur = windowStart
    while (D(cur) <= D(windowEnd)) {
      const d = new Date(D(cur))
      const last = iso(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
      const stop = D(last) < D(windowEnd) ? last : windowEnd
      months.push({
        label: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()],
        left: pct(cur),
        width: pct(addDays(stop, 1)) - pct(cur),
      })
      cur = addDays(stop, 1)
    }
  }

  const dragEnabled = props.editing && props.mode !== "monthly"
  const addMode = props.mode === "custom" && props.editing

  // Add-by-click is only valid on an empty stretch inside the flight, so the cursor stops
  // advertising it elsewhere.
  const addableAt = (d: string) =>
    D(d) >= D(flight.start) &&
    // A new window cannot start in the past, so the empty stretch behind today is not a
    // place to drop one.
    D(d) >= D(props.today) &&
    D(d) <= D(flight.end) &&
    !segments.some((s) => D(d) >= D(s.start) && D(d) <= D(s.end))

  return (
    <div>
      <div style={{ position: "relative" }}>
        <div className="seg-timeline__ruler">
          {months.map((m, i) => (
            <div key={i} className="seg-timeline__month" style={{ left: m.left + "%", width: m.width + "%" }}>
              {m.label}
            </div>
          ))}
        </div>

        <div
          ref={ref}
          className={`seg-timeline__track${addMode && canAddHere ? " seg-timeline__track--adding" : ""}`}
          {...todo("wrong", "Window is passed as the flight, so OUTSIDE FLIGHT bands never render. Needs an engagement window_start.")}
          onMouseMove={(e) => {
            if (!addMode) {
              if (canAddHere) setCanAddHere(false)
              return
            }
            const ok = addableAt(dateAtX(e.clientX))
            if (ok !== canAddHere) setCanAddHere(ok)
          }}
          onMouseLeave={() => setCanAddHere(false)}
          onClick={(e) => {
            if (!addMode) return
            const d = dateAtX(e.clientX)
            if (addableAt(d)) props.onAddAt(d)
          }}
        >
          <NoSpend left={0} width={pct(flight.start)} label="Outside flight" />
          <NoSpend
            left={pct(addDays(flight.end, 1))}
            width={100 - pct(addDays(flight.end, 1))}
            label="Outside flight"
          />

          {segments.map((s, i) => {
            const prev = segments[i - 1]
            const gap =
              prev && D(s.start) > D(prev.end) + DAY ? (
                <NoSpend
                  key={"g" + s.id}
                  left={pct(addDays(prev.end, 1))}
                  width={pct(s.start) - pct(addDays(prev.end, 1))}
                  label={dayCount(addDays(prev.end, 1), addDays(s.start, -1)) + "d gap"}
                />
              ) : null

            return (
              <span key={s.id}>
                {gap}
                <Block
                  s={s}
                  left={pct(s.start)}
                  width={pct(addDays(s.end, 1)) - pct(s.start)}
                  selected={s.id === props.selectedId}
                  onSelect={() => props.onSelect(s.id)}
                  dragEnabled={dragEnabled}
                  onDragStart={(edge) => setDrag({ id: s.id, edge })}
                  canDelete={props.editing && props.mode === "custom"}
                  onDelete={() => props.onDelete(s.id)}
                  fundedDays={props.fundedDays}
                  scheduledRanges={props.scheduledRanges}
                  today={props.today}
                />
              </span>
            )
          })}
        </div>

        {props.keyDates.map((m) => (
          <KeyHandle key={m.k} m={m} height={90} left={pct(m.endOfDay ? addDays(m.date, 1) : m.date)} />
        ))}
      </div>

      <div className="seg-strip__legend">
        <span>
          Engagement {fmtDay(windowStart)} – {fmtDay(windowEnd)}
        </span>
        <LegendKeys keyDates={props.keyDates} />
        {dragEnabled && (
          <span className="seg-timeline__hint">
            Drag a block edge to change its dates
            {props.mode === "custom" ? " · click an empty stretch to add a segment" : ""}
          </span>
        )}
      </div>
    </div>
  )
}

function NoSpend({ left, width, label }: { left: number; width: number; label: string }) {
  if (width <= 0) return null
  return (
    <div className="seg-timeline__nospend" title={label} style={{ left: left + "%", width: width + "%" }}>
      <span>{label}</span>
    </div>
  )
}

function Block({
  s,
  left,
  width,
  selected,
  onSelect,
  dragEnabled,
  onDragStart,
  canDelete,
  onDelete,
  fundedDays,
  scheduledRanges,
  today,
}: {
  s: Segment
  left: number
  width: number
  selected: boolean
  onSelect: () => void
  dragEnabled: boolean
  onDragStart: (edge: "start" | "end") => void
  canDelete: boolean
  onDelete: () => void
  fundedDays: number
  scheduledRanges: readonly ScheduledRange[]
  today: string
}) {
  const [hover, setHover] = useState(false)
  const days = dayCount(s.start, s.end)
  // Same rule as the card: an elapsed block is read-only, and a block straddling today
  // keeps its start grip locked because those days already happened.
  const past = D(s.end) < D(today)
  const canDrag = dragEnabled && !past
  const canDrop = canDelete && !past
  const edges = D(s.start) < D(today) ? (["end"] as const) : (["start", "end"] as const)

  return (
    <div
      role="button"
      tabIndex={0}
      className={`seg-block${selected ? " seg-block--on" : ""}`}
      style={{ left: left + "%", width: width + "%" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
    >
      <div
        className="seg-block__range"
        style={{ paddingRight: canDrop ? 14 : 0 }}
        title={past ? ENDED_TIP : undefined}
      >
        {past && <i className="bi bi-lock-fill seg-block__lock" />}
        {fmtRange(s.start, s.end)}
        {past ? " · Ended" : ""}
      </div>
      {/* Narrow blocks drop the daily rate rather than truncate it mid-value. */}
      <div className="seg-block__meta">
        {formatCurrency(s.amount)} · {days}d
        {width > 13 ? " · " + formatCurrency(s.amount / payDays(s, scheduledRanges, fundedDays)) + "/day" : ""}
      </div>

      {canDrop && (hover || selected) && (
        <button
          type="button"
          className="seg-block__drop"
          title="Delete segment"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          ✕
        </button>
      )}

      {canDrag &&
        edges.map((edge) => (
          <div
            key={edge}
            title="Drag to resize"
            className={`seg-block__grip${selected ? " seg-block__grip--on" : ""}`}
            style={{ [edge === "start" ? "left" : "right"]: 0 }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDragStart(edge)
            }}
          />
        ))}
    </div>
  )
}

/* ---------- key dates ---------- */

function KeyHandle({ m, left, height }: { m: KeyDate; left: number; height: number }) {
  const [hover, setHover] = useState(false)
  const align = left > 80 ? "right" : "left"

  return (
    <div className="seg-key" style={{ left: left + "%", height }}>
      <div
        className="seg-key__line"
        style={{
          left: m.solid ? -1 : -0.5,
          borderLeft: (m.solid ? "2px solid " : "1px dashed ") + m.color,
          opacity: hover ? 1 : 0.75,
        }}
      />
      <span
        className="seg-key__grab"
        tabIndex={0}
        aria-label={`${m.label} ${fmtDay(m.date)}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
      >
        <span
          className="seg-key__pip"
          style={{ background: hover ? m.color : "#fff", border: "1.5px solid " + m.color }}
        />
        {hover && <KeyBubble m={m} align={align} />}
      </span>
    </div>
  )
}

function KeyBubble({ m, align }: { m: KeyDate; align: "left" | "right" }) {
  return (
    <span className="seg-key__bubble" style={{ [align]: -6 }}>
      <b>{m.label}</b> · {fmtDay(m.date)}
    </span>
  )
}

function LegendKeys({ keyDates }: { keyDates: KeyDate[] }) {
  return (
    <span
      className="seg-strip__legend-keys"
      {...todo("data", "Next billing cycle marker never renders — the header exposes a countdown, not a date.")}
    >
      {keyDates.map((m) => (
        <span key={m.k} className="seg-strip__legend-key">
          <span
            className="seg-strip__legend-rule"
            style={{ borderLeft: (m.solid ? "2px solid " : "1px dashed ") + m.color }}
          />
          {m.label} {fmtDay(m.date)}
        </span>
      ))}
    </span>
  )
}

/**
 * The platform's grey-text-plus-info-icon treatment, upgraded to a hover bubble. Fixed from
 * the icon's own rect because the table's overflow-x scroller clips an absolute child.
 */
export function InfoTip({
  children,
  info,
  formula,
  method,
}: {
  children?: React.ReactNode
  /** What the thing is. Prose. */
  info?: React.ReactNode
  /** The rule, in the Ops Dashboard's frac{a}{b} notation. Typeset as maths. */
  formula?: string
  /** The one caveat the maths cannot express. */
  method?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [box, setBox] = useState<{ left: number; bottom: number; width: number } | null>(null)

  return (
    <span
      ref={ref}
      className="seg-infotip"
      onMouseEnter={() => {
        if (!ref.current) return
        const r = ref.current.getBoundingClientRect()
        const w = formula || method ? 340 : 250
        setBox({
          left: Math.max(10, Math.min(window.innerWidth - w - 10, r.right - w)),
          bottom: window.innerHeight - r.top + 7,
          width: w,
        })
      }}
      onMouseLeave={() => setBox(null)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        color: "var(--slate-400)",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      <i className="bi bi-info-circle" style={{ fontSize: 11, color: box ? "var(--slate-600)" : "inherit" }} />
      {children}
      {box && (
        <span
          style={{
            position: "fixed",
            left: box.left,
            bottom: box.bottom,
            width: box.width,
            zIndex: 60,
            padding: "9px 11px",
            background: "#fff",
            border: "1px solid var(--border-card)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-popover)",
            color: "var(--slate-600)",
            fontSize: "var(--fs-caption)",
            fontWeight: 400,
            lineHeight: 1.45,
            whiteSpace: "normal",
            textAlign: "left",
            pointerEvents: "none",
          }}
        >
          {info}
          {(formula || method) && (
            <>
              {!!info && <span className="seg-tip__rule" />}
              <span className="seg-tip__label">How this is calculated</span>
              {formula && <Formula source={formula} />}
              {method && <span className={formula ? "seg-tip__caveat" : undefined}>{method}</span>}
            </>
          )}
        </span>
      )}
    </span>
  )
}
